import React, { SyntheticEvent } from 'react';
import { nucleusConstants } from '@lifesize/nucleus';
import { Field, FormikErrors, FormikTouched, FormikValues } from 'formik';
import classnames from 'classnames';
import intl from 'react-intl-universal';
import { isPermanentMeeting } from 'utils/meetingsUtils';
import { DUPLICATE_MEETING_NAME_ERROR, OTHER_MEETING_NAME_ERROR } from 'constants/meetingConstants';
import RadioButton from 'components/RadioButton/RadioButton';
import RadioButtonGroup from 'components/RadioButton/RadioButtonGroup';
import * as formStyles from 'components/Modals/ModalForms.scss';
import { MEETING_FORM_FIELD } from 'components/Modals/MeetingsModal/FormFields';
import ToggleButton from 'components/ToggleButton/ToggleButton';
import { Tooltip } from 'components/tooltip/Tooltip';
import Icon from 'components/Common/Icon';
import { directoryHidingOverride } from 'constants/localStorageConsts';

interface Props {
  canCreateOneTimeMeeting: boolean;
  errors: FormikErrors<FormikValues>;
  handleChange: Function;
  hasDirectoryHidingFeature: boolean;
  isAdd: boolean;
  setFieldValue: Function;
  touched: FormikTouched<FormikValues>;
  values: FormikValues;
  visible: boolean;
}

// With HTML formatting for line wrap
const getNameError = (error: string, duplicate: string = '') => {
  if (error === DUPLICATE_MEETING_NAME_ERROR) {
    return intl.getHTML('duplicateMeetingNameError', { value: duplicate });
  }
  if (error === OTHER_MEETING_NAME_ERROR) {
    return intl.getHTML('nameMeetingError');
  }
  return error;
};

const getHideMeetingLabel = (labelText: string, toolTipText: string, isDisabled: boolean) => {
  return (
    <>
      <span className={classnames(isDisabled && formStyles.disabled)}>{labelText}</span>
      <Tooltip text={toolTipText} stylesWrapper={{ lineHeight: 1, marginLeft: '8px' }}>
        <Icon classes={['icon-info', formStyles.tooltipButtonIcon]}/>
      </Tooltip>
    </>
  );
};

class GeneralTab extends React.PureComponent<Props> {

  render(): React.ReactNode {
    const {
      canCreateOneTimeMeeting,
      errors,
      handleChange,
      hasDirectoryHidingFeature,
      isAdd,
      setFieldValue,
      touched,
      values,
      visible
    } = this.props;
    const meetingTypeTouched = touched.meetingType as boolean; // _get(touched, 'meetingType',  false);
    const meetingTypeError = errors.meetingType; // _get(errors, 'meetingType',  undefined);
    const nameTouched = touched.name;
    const nameError = errors.name;
    const pinTouched = touched.pin;
    const pinError = errors.pin;
    const descriptionTouched = touched.description;
    const descriptionError = errors.description;

    const handleMeetingTypeChange = (event: SyntheticEvent) => {
      const input = event.currentTarget as HTMLInputElement;
      if (values.meetingType !== input.value) {
        setFieldValue(MEETING_FORM_FIELD.HIDDEN, input.value === nucleusConstants.MEETING_TYPE_ONE_TIME, false);
        handleChange({ target: { name: 'meetingType', value: input.value }});
      }
    };

    const canHideMeeting = isPermanentMeeting(values[MEETING_FORM_FIELD.MEETING_TYPE]);

    return (
      <>
        <div className={formStyles.logicalGroup}>
          {isAdd &&
            <div className={formStyles.formGroup}>
              <label className={formStyles.labelLarge}>{intl.get('meetingType')}</label>
              <RadioButtonGroup
                className={formStyles.inputContainer}
                id="meetingType"
                value={values.meetingType}
                error={meetingTypeError as string}
                touched={meetingTypeTouched}
              >
                {canCreateOneTimeMeeting ?
                  <Field
                    component={RadioButton}
                    hidenow={!visible}
                    id={nucleusConstants.MEETING_TYPE_ONE_TIME}
                    label={intl.get('oneTimeMeeting')}
                    name={MEETING_FORM_FIELD.MEETING_TYPE}
                    onChange={handleMeetingTypeChange}
                  />
                  : null}
                <Field
                  component={RadioButton}
                  hidenow={!visible}
                  id={nucleusConstants.MEETING_TYPE_VMR}
                  label={intl.get('permanentMeeting')}
                  name={MEETING_FORM_FIELD.MEETING_TYPE}
                  onChange={handleMeetingTypeChange}
                />
              </RadioButtonGroup>
            </div>
          }

          <div id="name" className={classnames(formStyles.formGroup, formStyles.hasValidation)}>
            <label className={formStyles.labelLarge}>{intl.get('meetingName')}</label>
            <div className={formStyles.inputContainer}>
              <Field
                className={formStyles.input}
                maxLength={32}
                name={MEETING_FORM_FIELD.NAME}
                type="text"
              />
              <ul className={formStyles.inputMessages}>
                {nameTouched && nameError ? (
                  <li className={formStyles.error}>{getNameError(nameError.toString(), values.name)}</li>
                ) : null}
              </ul>
            </div>
          </div>

          <div id="passcode" className={classnames(formStyles.formGroup, formStyles.hasValidation)}>
            <label className={formStyles.labelLarge}>{intl.get('passcode_optional')}</label>
            <div className={formStyles.inputContainer}>
              <Field
                className={formStyles.input}
                type="text"
                name={MEETING_FORM_FIELD.PIN}
              />
              <ul className={formStyles.inputMessages}>
                {pinTouched && pinError ? (
                  <li className={formStyles.error}>{errors.pin}</li>
                ) : null}
              </ul>
            </div>
          </div>

          <div id="description" className={classnames(formStyles.formGroup, formStyles.hasValidation)}>
            <label className={formStyles.labelLarge}>{intl.get('description_optional')}</label>
            <div className={formStyles.inputContainer}>
              <Field
                className={formStyles.textarea}
                component="textarea"
                name={MEETING_FORM_FIELD.DESCRIPTION}
              />
              <ul className={formStyles.inputMessages}>
                {descriptionTouched && descriptionError ? (
                  <li className={formStyles.error}>{descriptionError}</li>
                ) : null}
              </ul>
            </div>
          </div>
        </div>
        {(directoryHidingOverride || hasDirectoryHidingFeature) &&
          <div className={formStyles.logicalGroup}>
            <div className={formStyles.formGroup}>
              <Field
                classNameLabel={formStyles.toggleLabelLarge}
                component={ToggleButton}
                disabled={!canHideMeeting}
                id={MEETING_FORM_FIELD.HIDDEN}
                label={getHideMeetingLabel(intl.get('inputLabelHideMeeting'), canHideMeeting ? intl.get('tooltipHideMeeting') : intl.get('tooltipHideMeetingDefault'), !canHideMeeting)}
                leftLabel={true}
                name={MEETING_FORM_FIELD.HIDDEN}
              />
            </div>
          </div>
        }
      </>
    );
  }
}

export default GeneralTab;
